import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { Alert, Badge, Collapse, Spinner, Table } from 'react-bootstrap';
import { ChevronDown, ChevronRight } from 'react-bootstrap-icons';
import {
  getKitStatus,
  getProductDetailsDirect,
} from '../../clients/product_client';
import { getUsersForOrder } from '../../clients/user_client';
import { logError, logInfo } from '../../config_and_helpers/logger';
import {
  applyStockItemTableUpdate,
  formatBottleneckCopy,
  formatCompleteKitsSummary,
  normalizeKitRecipeRows,
  resolveCompleteKitsCount,
  resolveKitBottleneck,
  resolveRelatedPartProduct,
} from '../../utils/kitInventory';
import StockItemTable from './StockItemTable';
import './ProductComponentStyle.css';

const LOG_CONTEXT = 'KitInventoryPanel';

/**
 * Read-only kit recipe + related parts for the inventory product modal.
 *
 * Recipe components belong to this kit CAT#. Related parts show all stock
 * lots of those components once (not grouped by kit lot), using the same
 * `StockItemTable` as a normal CAT#. Whole-kit In Use stays on the kit lots
 * table below. Each part’s `StockItemTable` starts collapsed. Component names
 * are plain text — this panel does not open a nested inventory modal.
 *
 * @param {Object} props
 * @param {Object} props.product
 * @param {string} props.token
 * @param {boolean} [props.isSupplier]
 * @param {Array} [props.userSelectList] - Parent-owned opener list; omit to fetch here
 * @param {boolean} [props.isLoadingUsers]
 * @param {Function} [props.onOpeningHistoryChange]
 * @param {Function} [props.onComponentStockSync] - After a related-part lot
 *   create/update/delete that can change completable-kit count. Parent should
 *   re-read kit get_details and force-apply lots.
 * @param {number} [props.refreshSignal] - Increment to reload related-part lots and
 *   kit status. The parent bumps this after a kit lot is opened as a unit or
 *   un-marked, because the backend cascades that to the component lots and those
 *   rows live here.
 * @param {string|null} [props.refreshReason] - Why the parent bumped the signal,
 *   e.g. `kit-lot-opened` or `kit-lot-closed`. A close reloads related-part lots
 *   so a component that left In Use drops its open date and opener.
 * @param {Array|null} [props.storageNodes] - Lab storage tree for placing related-part lots.
 */
const KitInventoryPanel = ({
  product,
  token,
  isSupplier = false,
  userSelectList,
  isLoadingUsers: parentLoadingUsers = false,
  onOpeningHistoryChange,
  onComponentStockSync,
  refreshSignal = 0,
  refreshReason = null,
  storageNodes = null,
}) => {
  const [kitStatus, setKitStatus] = useState(null);
  const isViewer = useIsLabViewer();
  const componentByIdRef = useRef({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [componentById, setComponentById] = useState({});
  const [isRefreshingParts, setIsRefreshingParts] = useState(false);
  const [expandedPartIds, setExpandedPartIds] = useState(() => new Set());
  const [fetchedUsers, setFetchedUsers] = useState([]);
  const [fetchingUsers, setFetchingUsers] = useState(false);

  const parentOwnsUsers = userSelectList !== undefined;

  const productId = product?.id ?? null;

  const loadKitStatus = useCallback(async () => {
    if (!token || !productId) return;
    setLoading(true);
    setError(null);
    logInfo(LOG_CONTEXT, 'panel load start', { productId });
    try {
      const status = await getKitStatus(token, productId);
      setKitStatus(status && typeof status === 'object' ? status : null);
      const rows = normalizeKitRecipeRows(status);
      logInfo(LOG_CONTEXT, 'panel load success', {
        productId,
        componentCount: rows.length,
        effectiveStock: resolveCompleteKitsCount(status),
      });
    } catch (err) {
      logError(LOG_CONTEXT, 'panel load failed', {
        productId,
        status: err?.response?.status ?? null,
      });
      setError('Failed to load kit recipe');
      setKitStatus(null);
    } finally {
      setLoading(false);
    }
  }, [token, productId]);

  useEffect(() => {
    loadKitStatus();
  }, [loadKitStatus]);

  const recipeRows = useMemo(
    () => normalizeKitRecipeRows(kitStatus),
    [kitStatus]
  );

  const recipeIdsKey = recipeRows.map(row => String(row.id)).join(',');

  // Also re-runs on `refreshSignal` so the component lots the backend opened or
  // closed alongside a kit lot show up without a page refresh.
  useEffect(() => {
    if (!token || !recipeIdsKey) {
      setComponentById({});
      return;
    }
    let cancelled = false;
    const ids = recipeIdsKey.split(',').filter(Boolean);
    setIsRefreshingParts(true);
    logInfo(LOG_CONTEXT, 'component details start', {
      productId,
      componentCount: ids.length,
      refreshSignal,
      refreshReason,
    });
    Promise.all(
      ids.map(async id => {
        const details = await getProductDetailsDirect(token, id);
        if (!details) {
          logError(LOG_CONTEXT, 'component details missing', {
            productId,
            componentId: id,
          });
        }
        const productDetails =
          details && typeof details === 'object'
            ? details
            : { id, items: [] };
        const items = Array.isArray(productDetails.items)
          ? productDetails.items
          : [];
        return {
          id,
          details: { ...productDetails, id: productDetails.id ?? id, items },
        };
      })
    )
      .then(results => {
        if (cancelled) return;
        const next = {};
        results.forEach(entry => {
          next[entry.id] = entry.details;
        });
        setComponentById(next);
        const lotCount = results.reduce(
          (sum, entry) => sum + (entry.details.items || []).length,
          0
        );
        const openLotCount = results.reduce(
          (sum, entry) =>
            sum + (entry.details.items || []).filter(item => item?.in_use).length,
          0
        );
        logInfo(LOG_CONTEXT, 'component details success', {
          productId,
          componentCount: results.length,
          lotCount,
          openLotCount,
          refreshSignal,
          refreshReason,
        });
        setIsRefreshingParts(false);
      })
      .catch(err => {
        if (cancelled) return;
        logError(LOG_CONTEXT, 'component details failed', {
          productId,
          refreshSignal,
          refreshReason,
          status: err?.response?.status ?? null,
        });
        setIsRefreshingParts(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, productId, recipeIdsKey, refreshSignal, refreshReason]);

  useEffect(() => {
    if (isSupplier || parentOwnsUsers || !token) {
      if (!parentOwnsUsers) {
        setFetchedUsers([]);
      }
      return;
    }
    let cancelled = false;
    setFetchingUsers(true);
    logInfo(LOG_CONTEXT, 'users fetch start', { productId });
    getUsersForOrder(token)
      .then(users => {
        if (cancelled) return;
        if (Array.isArray(users) && !users.error) {
          setFetchedUsers(
            users.map(user => ({
              value: user.id,
              label: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
            }))
          );
          logInfo(LOG_CONTEXT, 'users fetch success', {
            productId,
            userCount: users.length,
          });
        } else {
          setFetchedUsers([]);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFetchedUsers([]);
          logError(LOG_CONTEXT, 'users fetch failed', { productId });
        }
      })
      .finally(() => {
        if (!cancelled) setFetchingUsers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSupplier, parentOwnsUsers, token, productId]);

  const resolvedUsers = parentOwnsUsers ? userSelectList : fetchedUsers;
  const resolvedLoadingUsers = parentOwnsUsers
    ? Boolean(parentLoadingUsers)
    : fetchingUsers;

  /**
   * Re-read `kit_status` without touching the loading state or notifying the
   * parent, so complete-kit counts stay accurate after component stock moves.
   */
  const loadKitStatusQuietly = useCallback(
    (reason = 'related part') => {
      if (!token || !productId) return;
      getKitStatus(token, productId)
        .then(status => {
          if (status && typeof status === 'object') {
            setKitStatus(status);
            logInfo(LOG_CONTEXT, 'kit status refresh', {
              productId,
              reason,
              effectiveStock: resolveCompleteKitsCount(status),
            });
          }
        })
        .catch(err => {
          logError(LOG_CONTEXT, 'kit status refresh failed', {
            productId,
            reason,
            status: err?.response?.status ?? null,
          });
        });
    },
    [productId, token]
  );

  const reloadRelatedPartDetails = useCallback(
    componentId => {
      if (!token || componentId == null) return;
      getProductDetailsDirect(token, componentId).then(details => {
        if (!details || typeof details !== 'object') return;
        const items = Array.isArray(details.items) ? details.items : [];
        const nextDetails = {
          ...details,
          id: details.id ?? componentId,
          items,
        };
        setComponentById(prev => ({
          ...prev,
          [componentId]: nextDetails,
          [String(componentId)]: nextDetails,
        }));
        logInfo(LOG_CONTEXT, 'related part details reloaded', {
          productId,
          componentId,
          itemCount: items.length,
          stock: details.stock,
        });
      });
    },
    [productId, token]
  );

  const refreshKitStatusQuietly = useCallback(() => {
    if (typeof onOpeningHistoryChange === 'function') {
      onOpeningHistoryChange();
    }
    loadKitStatusQuietly('related part');
  }, [loadKitStatusQuietly, onOpeningHistoryChange]);

  /**
   * Related-part lot mutation that can change completable-kit count: re-read
   * kit_status, parent kit details, and this component's lots.
   */
  const refreshAfterRelatedPartStockChange = useCallback(
    (componentId, reason = 'related part stock') => {
      if (typeof onComponentStockSync === 'function') {
        onComponentStockSync({ componentId, kitProductId: productId });
      } else if (typeof onOpeningHistoryChange === 'function') {
        onOpeningHistoryChange();
      }
      loadKitStatusQuietly(reason);
      reloadRelatedPartDetails(componentId);
    },
    [
      loadKitStatusQuietly,
      onComponentStockSync,
      onOpeningHistoryChange,
      productId,
      reloadRelatedPartDetails,
    ]
  );

  componentByIdRef.current = componentById;

  // The kit lot open/close already refetched the kit product upstream.
  // Related-part rows live here, so reload the ones already on screen. That
  // picks up component lots the server closed, including a cleared open date
  // and opener. Do not call back up — that would loop.
  useEffect(() => {
    if (!refreshSignal) return;
    loadKitStatusQuietly(refreshReason || 'kit lot opening changed');
    const seen = new Set();
    Object.values(componentByIdRef.current || {}).forEach(row => {
      if (row?.id == null || seen.has(String(row.id))) return;
      seen.add(String(row.id));
      reloadRelatedPartDetails(row.id);
    });
    logInfo(LOG_CONTEXT, 'related parts reloaded after kit lot opening change', {
      productId,
      reason: refreshReason,
      componentCount: seen.size,
    });
  }, [refreshSignal, refreshReason, loadKitStatusQuietly, reloadRelatedPartDetails, productId]);

  const handleRelatedPartStockItemsUpdate = useCallback(
    (componentId, payload) => {
      if (componentId == null) return;
      setComponentById(prev => {
        const current =
          prev[componentId] || prev[String(componentId)] || { id: componentId };
        const nextItems = applyStockItemTableUpdate(current.items, payload);
        logInfo(LOG_CONTEXT, 'related part table update', {
          productId,
          componentId,
          deleteItem: Boolean(payload?.deleteItem),
          replaceSingleItem: Boolean(payload?.replaceSingleItem),
          itemCount: nextItems.length,
        });
        return {
          ...prev,
          [componentId]: {
            ...current,
            id: current.id ?? componentId,
            items: nextItems,
          },
        };
      });
      refreshAfterRelatedPartStockChange(componentId);
    },
    [productId, refreshAfterRelatedPartStockChange]
  );

  const toggleRelatedPartTable = useCallback(
    componentId => {
      if (componentId == null) return;
      const key = String(componentId);
      setExpandedPartIds(prev => {
        const next = new Set(prev);
        const expanded = !next.has(key);
        if (expanded) next.add(key);
        else next.delete(key);
        logInfo(LOG_CONTEXT, 'related part table toggled', {
          productId,
          componentId,
          expanded,
        });
        return next;
      });
    },
    [productId]
  );

  const completeCount = resolveCompleteKitsCount(kitStatus);
  const bottleneck = resolveKitBottleneck(kitStatus);
  const bottleneckCopy = formatBottleneckCopy(bottleneck);

  const renderComponentName = row => (
    <>
      <strong>{row.name}</strong>
      {row.catNum ? (
        <div className="kit-inventory-panel__cat text-muted">{row.catNum}</div>
      ) : null}
    </>
  );

  if (loading && !kitStatus) {
    return (
      <div className="kit-inventory-panel glass-panel mb-3">
        <div className="d-flex align-items-center justify-content-center py-3">
          <Spinner animation="border" size="sm" role="status" />
          <span className="ms-2">Loading kit recipe…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        variant="danger"
        className="mb-3"
        dismissible
        onClose={() => setError(null)}
      >
        {error}
      </Alert>
    );
  }

  return (
    <div className="kit-inventory-panel glass-panel mb-3">
      <div className="kit-inventory-panel__summary">
        <h5 className="kit-inventory-panel__title mb-1">Kit</h5>
        <p className="kit-inventory-panel__complete mb-1">
          {formatCompleteKitsSummary(completeCount)}
        </p>
        {bottleneckCopy ? (
          <p className="kit-inventory-panel__bottleneck mb-1">
            {bottleneckCopy}
          </p>
        ) : null}
        <p className="kit-inventory-panel__hint mb-0">
          {isViewer
            ? 'Related parts belong to this kit CAT#. Expand a part to see its stock table, or review the kit lots below.'
            : 'Related parts belong to this kit CAT#. Expand a part to edit its stock table, or open the whole kit as a unit on the lots table below.'}
        </p>
      </div>

      <div className="kit-inventory-panel__recipe mt-3">
        <h6 className="kit-inventory-panel__section-title">Recipe</h6>
        {recipeRows.length === 0 ? (
          <p className="kit-inventory-panel__empty mb-0">
            {isViewer
              ? 'No components on this kit yet.'
              : 'No components on this kit yet. Change the recipe with Edit product.'}
          </p>
        ) : (
          <div className="glass-table-wrap">
            <Table
              striped
              bordered
              hover
              size="sm"
              className="mb-0 kit-inventory-panel__recipe-table"
            >
              <thead>
                <tr className="text-center">
                  <th>Component</th>
                  <th>Qty per kit</th>
                  <th>Complete kits</th>
                  <th>Required</th>
                </tr>
              </thead>
              <tbody>
                {recipeRows.map(row => (
                  <tr key={row.id} className="text-center align-middle">
                    <td className="text-start">{renderComponentName(row)}</td>
                    <td>{row.quantityPerKit}</td>
                    <td>{row.availableKits}</td>
                    <td>
                      {row.isRequired ? (
                        <Badge bg="primary">Required</Badge>
                      ) : (
                        <Badge bg="secondary">Optional</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {!isViewer ? (
          <p className="kit-inventory-panel__footer-hint mb-0 mt-2">
            Change recipe with Edit product.
          </p>
        ) : null}
      </div>

      {!isSupplier && recipeRows.length > 0 && (
        <div className="kit-inventory-panel__parts mt-3">
          <h6 className="kit-inventory-panel__section-title">
            Related parts
            {isRefreshingParts && (
              <span className="kit-inventory-panel__parts-refresh text-muted">
                <Spinner animation="border" size="sm" role="status" />
                <span className="ms-2">Updating part lots…</span>
              </span>
            )}
          </h6>
          {recipeRows.map(row => {
            const partProduct = resolveRelatedPartProduct(row, componentById);
            const lots = Array.isArray(partProduct.items)
              ? partProduct.items.filter(Boolean)
              : [];
            const showBar =
              partProduct.units_per_sub_unit != null &&
              (partProduct.unit === 'Box' || partProduct.unit === 'Package');
            const partKey = String(row.id);
            const expanded = expandedPartIds.has(partKey);
            const tableId = `kit-related-part-table-${partKey}`;
            const lotNoun = lots.length === 1 ? 'lot' : 'lots';
            return (
              <div key={row.id} className="kit-inventory-panel__part">
                <button
                  type="button"
                  className="kit-inventory-panel__part-toggle"
                  onClick={() => toggleRelatedPartTable(row.id)}
                  aria-expanded={expanded}
                  aria-controls={tableId}
                >
                  {expanded ? (
                    <ChevronDown
                      className="kit-inventory-panel__part-chevron"
                      size={16}
                    />
                  ) : (
                    <ChevronRight
                      className="kit-inventory-panel__part-chevron"
                      size={16}
                    />
                  )}
                  <span className="kit-inventory-panel__part-head-text">
                    <strong>{row.name}</strong>
                    {row.catNum ? (
                      <span className="kit-inventory-panel__cat text-muted ms-2">
                        {row.catNum}
                      </span>
                    ) : null}
                    <span className="text-muted ms-2">
                      ×{row.quantityPerKit}
                    </span>
                    <span className="text-muted ms-2">
                      · {lots.length} {lotNoun}
                    </span>
                  </span>
                </button>
                <Collapse in={expanded} unmountOnExit>
                  <div id={tableId}>
                    <div className="glass-table-wrap">
                      <StockItemTable
                        product={partProduct}
                        items={lots}
                        updateStockItems={payload =>
                          handleRelatedPartStockItemsUpdate(row.id, payload)
                        }
                        showBar={showBar}
                        showHeader={false}
                        setTotalItemStock={() => {}}
                        updateProductLogs={() => {}}
                        updateProductStock={() => {}}
                        addNewItem={false}
                        userSelectList={resolvedUsers}
                        isLoadingUsers={resolvedLoadingUsers}
                        onOpeningHistoryChange={refreshKitStatusQuietly}
                        useGlassTable
                        storageNodes={storageNodes}
                      />
                    </div>
                  </div>
                </Collapse>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default KitInventoryPanel;
